<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <div class="field field-scope">
          <label class="field-lbl">ประเภท</label>
          <div class="chip-group">
            <label class="chip" :class="{ 'chip-active': itemsType.includes('01') }">
              <input type="checkbox" value="01" v-model="itemsType" />
              <span>รักษาทั่วไป</span>
            </label>
            <label class="chip" :class="{ 'chip-active': itemsType.includes('02') }">
              <input type="checkbox" value="02" v-model="itemsType" />
              <span>แพ็กเกจจัดฟัน</span>
            </label>
            <label class="chip" :class="{ 'chip-active': itemsType.includes('03') }">
              <input type="checkbox" value="03" v-model="itemsType" />
              <span>ยา และอุปกรณ์</span>
            </label>
          </div>
        </div>

        <b-button
          variant="primary"
          class="btn-primary-lg"
          @click="fetchReport"
        >
          แสดงข้อมูล
        </b-button>
      </div>

      <div class="fc-row fc-row-secondary">
        <div class="field">
          <label class="field-lbl">สถานะ</label>
          <b-form-select
            v-model="itemsStatus"
            :options="options"
            size="sm"
            class="field-input-native"
          ></b-form-select>
        </div>
      </div>
    </div>

    <div class="toolbar">
      <span></span>
      <div class="toolbar-right">
        <b-dropdown size="sm" variant="outline-success" right>
          <template #button-content>
            <i class="fas fa-download"></i>
          </template>
          <b-dropdown-item @click="excelExport('OverdueItemTable')">
            <i class="fas fa-file-excel mr-2"></i>Excel
          </b-dropdown-item>
          <b-dropdown-item @click="pdfExport('OverdueItemTable')">
            <i class="fas fa-file-pdf mr-2"></i>PDF
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>

    <b-table
      id="OverdueItemTable"
      ref="OverdueItemTable"
      class="report-table"
      small
      sort-icon-left
      head-variant="dark"
      :busy="tableBusy"
      :items="filteredOverdueList"
      :fields="fields"
      sort-by="creationDt"
      v-model="visibleRows"
      :sort-desc="true"
      :responsive="true"
      show-empty
      sticky-header="calc(100vh - 210px)"
    >
      <template #cell(index)="row">{{ row.index + 1 }}.</template>
      <template #cell(creationDt)="row">{{
        formatDate(row.item.creationDt)
      }}</template>
      <template #cell(time)="row">{{
        formatTime(row.item.creationDt)
      }}</template>

      <template #cell(treatType)="row">{{ formatType(row.value) }}</template>
      <template #cell(overdueDelFlag)="row">{{
        row.value ? "ลบแล้ว" : "ปกติ"
      }}</template>
      <template #cell(overdueDelFullName)="row">{{
        getFullname(row.item)
      }}</template>
      <template #cell(overdueDelDate)="row">{{
        formatDate(row.value) + " " + formatTime(row.value)
      }}</template>

      <template #custom-foot>
        <b-tr variant="secondary" class="text-bold text-right">
          <td class="text-left">รวม</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>

          <td>{{ formatNumber(sum("amount")) }}</td>
          <td>{{ formatNumber(sum("paid")) }}</td>
          <td>{{ formatNumber(sum("overdue")) }}</td>
          <td></td>
          <td></td>
          <td></td>
        </b-tr>
      </template>
      <template #empty="">
        <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
      </template>
    </b-table>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import moment from "moment";

import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";

export default {
  name: "OverdueItemReport",
  components: {
    Dialog,
    Loading,
  },
  data() {
    return {
      isLoading: false,

      dateRange: [],
      //table
      tableBusy: false,
      overdueList: [],
      fields: [
        {
          key: "index",
          label: "",
        },
        {
          key: "creationDt",
          label: "วันที่",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "time",
          label: "เวลา",
          class: "narrow-spacing",
        },
        {
          key: "dn",
          label: "DN.",
          class: "narrow-spacing",
        },
        {
          key: "patientFullName",
          label: "ชื่อคนไข้",
          class: "narrow-spacing",
        },
        {
          key: "itemName",
          label: "รายการรักษา",
          class: "narrow-spacing",
        },
        {
          key: "treatType",
          label: "ประเภท",
          class: "narrow-spacing",
        },
        {
          key: "doctor",
          label: "แพทย์",
          class: "narrow-spacing",
          formatter(value, key, item) {
            return `${item.doctorFirstName} ${item.doctorLastName}`;
          },
        },
        {
          key: "amount",
          label: "ค่ารักษาสุทธิ",
          class: "text-right",
          formatter: "formatNumber",
        },
        {
          key: "paid",
          label: "ชำระ",
          class: "text-right",
          formatter: "formatNumber",
        },
        {
          key: "overdue",
          label: "ค้างจ่าย",
          class: "text-right text-danger",
          formatter: "formatNumber",
        },
        {
          key: "overdueDelFlag",
          label: "สถานะ",
          class: "",
        },
        {
          key: "overdueDelFullName",
          label: "ลบโดย",
          class: "narrow-spacing",
        },
        {
          key: "overdueDelDate",
          label: "ลบเมื่อ",
          class: "narrow-spacing",
        },
      ],
      visibleRows: [],
      itemsType: [],
      options: [
        { value: 0, text: "ทั้งหมด" },
        { value: 1, text: "ปกติ" },
        { value: 2, text: "ลบแล้ว" },
      ],
      itemsStatus: 1,
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      userInfo: "moduleUser/getUserInfo",
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    filteredOverdueList() {
      let list = this.overdueList.filter((i) => {
        if (this.itemsStatus === 0) return true;
        if (this.itemsStatus === 1) return !i.overdueDelFlag;
        if (this.itemsStatus === 2) return i.overdueDelFlag;
      });
      list = list.map((i) => {
        let _rowVariant = i.overdueDelFlag ? "secondary" : "";
        return { ...i, _rowVariant };
      });
      if (this.itemsType.length === 0) {
        return list;
      } else {
        return list.filter((i) => this.itemsType.includes(i.treatType));
      }
    },
    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
  },
  watch: {},
  methods: {
    ...mapActions({
      fetchOverdueItemsReport: "moduleReport/fetchOverdueItemsReport",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    init() {},
    moment() {
      return moment();
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      } else {
        return "";
      }
    },
    formatTime(date) {
      if (date) {
        return moment(date).locale("th").format("HH:mm");
      } else {
        return "";
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) return "";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatType(code) {
      switch (code) {
        case "01":
          return "รักษาทั่วไป";
        case "02":
          return "แพ็กเกจจัดฟัน";
        case "03":
          return "ยา และอุปกรณ์";
      }
    },
    getFullname(item) {
      if (item.overdueDelFirstName && item.overdueDelLastName) {
        return item.overdueDelFirstName + " " + item.overdueDelLastName;
      } else {
        return "";
      }
    },

    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    sum(arg) {
      return (
        this.visibleRows.reduce((accum, item) => {
          return accum + parseFloat(item[arg]);
        }, 0.0) || 0
      );
    },
    generatePDf() {
      eventBus.$emit(
        "generatePDF",
        this.$refs["ReceiptIncomeTable"].$el,
        this.$refs.header,
        this.$refs.footer
      );
    },

    sendEmail() {
      //รอ api
    },
    fetchReport() {
      if (this.dateFrom === "" || this.dateTo === "") return;

      this.setStateReportDateRange(this.dateRange);
      this.isLoading = true;
      this.fetchOverdueItemsReport({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
      })
        .then((res) => {
          this.overdueList = res.data.report;
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    excelExport(id) {
      exportTableToExcel({
        tableId: id,
        reportTitle: "รายงานรายการค้างชำระ",
        reportNameEn: "Overdue",
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });
    },
    pdfExport(id) {
      exportReportToPdf({
        tableId: id,
        reportTitle: "รายงานรายการค้างชำระ",
        reportNameEn: "Overdue",
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
  },
  mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
  },
  updated() {},
};
</script>

<style>
</style>
